// Small progressive enhancements. Every page still works as plain forms without this file.
import { safeCell } from '../src/lib/csv.js';
import { ask, closePop } from './popups.js';
import './theme-toggle.js';
import './haptics.js';
import './search.js';

// Product analytics for signed-in team members (never the public site, portal or forms): only when the server sent a key.
if (document.body.dataset.phKey) import('./analytics.js').then(m => m.start(document.body.dataset as Record<string, string>)).catch(() => { /* analytics must never break the page */ });

// ---------- browser storage follows the account and organisation in use ----------
// Keys are adab:<account>:<org>:<name>, so switching accounts or organisations never shows another's
// saved choices, and an account's keys go when it signs out of this browser.
const ACCOUNT = document.body.dataset.account ?? '';
const ORG = document.body.dataset.org ?? '';
const scoped = (name: string) => `adab:${ACCOUNT}:${ORG}:${name}`;
try {
  const signedIn = (document.body.dataset.accounts ?? '').split(' ').filter(Boolean);
  const fullySignedOut = document.body.dataset.signedIn === '0';
  if (signedIn.length || fullySignedOut) {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i)!;
      if (k === 'adab:active' && !fullySignedOut) continue;
      if (k.startsWith('adab:') && (fullySignedOut || !signedIn.includes(k.split(':')[1]!))) localStorage.removeItem(k);
    }
    localStorage.removeItem('inbox-list'); // from before keys were scoped
  }
  // Another tab switched account or organisation: say so here, so nothing is done in the wrong one.
  if (ACCOUNT) {
    const me = `${ACCOUNT}:${ORG}`;
    localStorage.setItem('adab:active', me);
    window.addEventListener('pageshow', () => { try { localStorage.setItem('adab:active', me); } catch { /* ignore */ } });
    window.addEventListener('storage', e => {
      if (e.key !== 'adab:active' || !e.newValue || e.newValue === me || document.querySelector('.tab-switched')) return;
      const note = document.createElement('div');
      note.className = 'tab-switched';
      note.setAttribute('role', 'alert');
      note.textContent = e.newValue.split(':')[0] === ACCOUNT ? 'You switched organisation in another tab.' : 'You switched account in another tab.';
      const reload = document.createElement('button');
      reload.type = 'button'; reload.className = 'secondary'; reload.textContent = 'Reload this page';
      reload.addEventListener('click', () => location.reload());
      note.append(reload);
      document.body.append(note);
    });
  }
} catch { /* storage blocked: nothing saved, nothing to tidy */ }

// ---- sidebar: collapse to a rail (remembered in a cookie the server reads), drawer on phones
const body = document.body;
const sidebar = document.getElementById('sidebar');
const toggle = document.querySelector<HTMLButtonElement>('[data-sidebar-toggle]');
toggle?.addEventListener('click', e => {
  // A mouse click should not leave a focus ring behind; keyboard focus stays visible.
  if (e.detail > 0) toggle.blur();
  const collapsed = body.dataset.sidebar !== 'collapsed';
  body.dataset.sidebar = collapsed ? 'collapsed' : 'open';
  document.cookie = `sidebar=${collapsed ? 'collapsed' : 'open'}; path=/; max-age=31536000; samesite=lax`;
  toggle.setAttribute('aria-expanded', String(!collapsed));
  const label = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  toggle.setAttribute('aria-label', label);
  toggle.title = label;
});

// ---- the banner stack sticks to the top of the page; sticky side panels sit below it (--banners-h)
const banners = document.querySelector<HTMLElement>('.banners');
if (banners) new ResizeObserver(() => document.documentElement.style.setProperty('--banners-h', `${banners.offsetHeight}px`)).observe(banners);

// ---- inbox: on wide screens the conversation list can be hidden to give the open chat more room (remembered per browser)
const inboxToggle = document.querySelector<HTMLButtonElement>('[data-inbox-toggle]');
const inboxEl = inboxToggle?.closest<HTMLElement>('.inbox');
if (inboxToggle && inboxEl) {
  const showList = (hidden: boolean) => {
    inboxEl.classList.toggle('list-hidden', hidden);
    inboxToggle.setAttribute('aria-expanded', String(!hidden));
    const label = hidden ? 'Show the conversation list' : 'Hide the conversation list';
    inboxToggle.setAttribute('aria-label', label);
    inboxToggle.title = label;
  };
  let saved = false;
  try { saved = localStorage.getItem(scoped('inbox-list')) === 'hidden'; } catch { /* storage blocked: start with the list shown */ }
  showList(saved);
  inboxToggle.hidden = false;
  inboxToggle.addEventListener('click', e => {
    if (e.detail > 0) inboxToggle.blur();
    const hidden = !inboxEl.classList.contains('list-hidden');
    showList(hidden);
    try { localStorage.setItem(scoped('inbox-list'), hidden ? 'hidden' : 'shown'); } catch { /* not remembered, still works */ }
  });
}

const navOpener = document.querySelector<HTMLButtonElement>('[data-nav-open]');
const closeDrawer = () => {
  if (!body.hasAttribute('data-nav-open')) return;
  body.removeAttribute('data-nav-open');
  navOpener?.setAttribute('aria-expanded', 'false');
  navOpener?.focus();
};
navOpener?.addEventListener('click', () => {
  body.setAttribute('data-nav-open', '');
  navOpener.setAttribute('aria-expanded', 'true');
  sidebar?.querySelector<HTMLElement>('a, button')?.focus();
});
document.querySelector('[data-nav-close]')?.addEventListener('click', closeDrawer);
document.addEventListener('keydown', e => { if (e.key === 'Escape') closeDrawer(); });
// In the phone drawer a category shows its pages beside it instead of opening the first one (and closing the drawer).
sidebar?.addEventListener('click', e => {
  const cat = (e.target as Element).closest<HTMLAnchorElement>('.sb-cat');
  if (!cat || !matchMedia('(max-width: 56rem)').matches || e.metaKey || e.ctrlKey || e.shiftKey) return;
  e.preventDefault();
  sidebar.dataset.cat = cat.dataset.cat;
  sidebar.querySelectorAll<HTMLElement>('.sb-cat').forEach(c => { if (c === cat) c.setAttribute('aria-current', 'true'); else c.removeAttribute('aria-current'); });
  sidebar.querySelectorAll<HTMLElement>('.sb-nav[data-cat]').forEach(n => { n.hidden = n.dataset.cat !== cat.dataset.cat; });
});

// ---- a sent form shows it is working, and cannot be sent twice by a double click
// (data-no-busy on the form or button skips it: sign out, switching account, anything instant)
document.addEventListener('submit', e => {
  const form = e.target as HTMLFormElement;
  if (e.defaultPrevented || (form.getAttribute('method') ?? '').toLowerCase() !== 'post') return;
  if (form.hasAttribute('data-no-busy') || (e as SubmitEvent).submitter?.hasAttribute('data-no-busy')) return;
  const button = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  setTimeout(() => { if (button && !e.defaultPrevented) { button.setAttribute('aria-busy', 'true'); button.disabled = true; button.dataset.sent = ''; } });
});
// back from another page (Stripe checkout, say): the browser restores the page as it was left, spinner and all
window.addEventListener('pageshow', e => {
  if (!e.persisted) return;
  for (const b of document.querySelectorAll<HTMLButtonElement>('button[data-sent]')) { b.removeAttribute('aria-busy'); b.disabled = false; delete b.dataset.sent; }
});
const $$ = <T extends Element = HTMLElement>(sel: string) => [...document.querySelectorAll<T>(sel as any)] as T[];


// form[data-confirm] asks first: see popups.ts.

$$<HTMLInputElement>('[data-check-all]').forEach(box => box.addEventListener('change', () => {
  $$<HTMLInputElement>(box.dataset.checkAll!).forEach(b => { b.checked = box.checked; });
}));

// CSV import: read the chosen file into the textarea so the form posts plain text.
$$<HTMLInputElement>('input[type=file][data-into]').forEach(inp => inp.addEventListener('change', async () => {
  const file = inp.files?.[0];
  // Not file.text(): that forces UTF-8, and a CSV from older Excel is Windows-1252, so é and £ would arrive as �.
  if (file) {
    const bytes = await file.arrayBuffer();
    let text: string;
    try { text = new TextDecoder('utf-8', { fatal: true }).decode(bytes); } catch { text = new TextDecoder('windows-1252').decode(bytes); }
    (document.querySelector(inp.dataset.into!) as HTMLTextAreaElement).value = text;
  }
  const name = inp.form?.querySelector<HTMLInputElement>('input[name=file_name]');
  if (file && name) name.value = file.name;
}));

// Repeatable rows (custom fields, conditions, WhatsApp params).
$$<HTMLButtonElement>('[data-add-row]').forEach(btn => btn.addEventListener('click', () => {
  const tpl = document.querySelector(btn.dataset.addRow!) as HTMLTemplateElement;
  tpl.parentElement!.insertBefore(tpl.content.cloneNode(true), tpl);
}));
document.addEventListener('click', e => {
  const t = e.target as HTMLElement;
  if (t.matches('[data-remove-row]')) t.closest('.rowitem')?.remove();
});

// Show only the trigger-value input matching the chosen automation trigger.
const trig = document.querySelector<HTMLSelectElement>('[name=trigger_type]');
if (trig) {
  const sync = () => $$<HTMLElement>('[data-trigger]').forEach(el => { el.hidden = el.dataset.trigger !== trig.value; });
  trig.addEventListener('change', sync);
  sync();
}

// Tabs that swap panels on one page (Integrations). Every link stands on its own — the server
// renders the tab it points at — so this only saves the round trip.
(() => {
  const nav = document.querySelector<HTMLElement>('[data-tabnav]');
  const links = nav ? [...nav.querySelectorAll<HTMLAnchorElement>('a[data-tab]')] : [];
  const panels = $$<HTMLElement>('[data-panel]');
  if (!links.length || !panels.length) return;
  const show = (id: string, url: string) => {
    panels.forEach(p => { p.hidden = p.dataset.panel !== id; });
    links.forEach(a => a.toggleAttribute('aria-current', a.dataset.tab === id));
    history.replaceState(null, '', url);
    document.getElementById(url.split('#')[1] ?? '')?.scrollIntoView();
  };
  links.forEach(a => a.addEventListener('click', e => { e.preventDefault(); show(a.dataset.tab!, a.getAttribute('href')!); }));
  // A link straight to something in another tab, like /connections#sync from an older bookmark.
  const deep = location.hash ? document.getElementById(location.hash.slice(1))?.closest<HTMLElement>('[data-panel]') : null;
  if (deep?.hidden) show(deep.dataset.panel!, location.href);
})();

// "Add to workflow" posts to the chosen workflow's own enrol URL.
document.querySelectorAll<HTMLFormElement>('form[data-enroll]').forEach(f => f.addEventListener('submit', () => {
  f.action = `/workflows/${f.querySelector<HTMLSelectElement>('[data-enroll-select]')!.value}/enroll`;
}));

// Stacked tables on phones hide their header row, so each cell carries its column's name instead
// (unless the view already gave it one). Self-explanatory cells (with a sub-line, ticks, actions) stay bare.
document.querySelectorAll<HTMLTableElement>('.stack-sm table').forEach(t => {
  const heads = [...(t.querySelector('tr:has(th)')?.children ?? [])].map(th =>
    th.classList.contains('check') || th.classList.contains('act') ? '' : (th.querySelector('.th-sort')?.childNodes[0]?.textContent ?? th.textContent ?? '').trim());
  t.querySelectorAll('tbody tr').forEach(tr => [...tr.children].forEach((td, i) => {
    if (td.hasAttribute('data-label') || td.hasAttribute('colspan') || td.querySelector('.cell-sub') || !heads[i]) return;
    td.setAttribute('data-label', heads[i]);
  }));
});

// Bulk bars: show once rows are ticked, with only the input the chosen action needs. Every table
// can also export the ticked rows as a CSV, straight from what is on the page.
document.querySelectorAll<HTMLElement>('[data-bulkbar]').forEach(bulkbar => {
  const scope = bulkbar.closest('form') ?? document;
  const boxes = () => [...scope.querySelectorAll<HTMLInputElement>('.pick')];
  const picks = () => boxes().filter(b => b.checked).length;
  const action = bulkbar.querySelector<HTMLSelectElement>('[data-bulk-action]');
  const allBtn = bulkbar.querySelector<HTMLButtonElement>('[data-select-all]');
  const allFlag = bulkbar.querySelector<HTMLInputElement>('[data-all-flag]');
  const sync = () => {
    const n = picks();
    const everyOnPage = n > 0 && n === boxes().length;
    if (!everyOnPage && allFlag) allFlag.value = '';
    bulkbar.hidden = n === 0;
    bulkbar.querySelector('[data-bulk-count]')!.textContent = allFlag?.value ? bulkbar.dataset.total! : String(n);
    if (allBtn) allBtn.hidden = !everyOnPage || Boolean(allFlag?.value);
    bulkbar.querySelectorAll<HTMLElement>('[data-for-action]').forEach(el => { el.hidden = el.dataset.forAction !== action?.value; });
    scope.querySelectorAll<HTMLInputElement>('[data-check-all]').forEach(all => { all.checked = everyOnPage; all.indeterminate = n > 0 && !everyOnPage; });
  };
  scope.addEventListener('change', e => {
    const t = e.target as HTMLInputElement;
    if (t.matches('[data-check-all]')) boxes().forEach(b => { b.checked = t.checked; });
    if (t.matches('.pick, [data-check-all], [data-bulk-action]')) sync();
  });
  allBtn?.addEventListener('click', () => { allFlag!.value = '1'; sync(); });
  bulkbar.querySelector('[data-clear-selected]')?.addEventListener('click', () => { boxes().forEach(b => { b.checked = false; }); sync(); });
  bulkbar.querySelector<HTMLButtonElement>('[data-export-selected]')?.addEventListener('click', ev => {
    const table = (scope as HTMLElement).querySelector?.('table') ?? document.querySelector('table');
    if (!table) return;
    // The same formula guard as the server's exports (src/lib/csv.ts).
    const cell = (el: Element) => `"${safeCell((el.textContent ?? '').replace(/\s+/g, ' ').trim()).replace(/"/g, '""')}"`;
    const keep = (row: Element) => [...row.children].filter(c => !['sel', 'check', 'act'].some(k => c.classList.contains(k)));
    const head = [...table.querySelectorAll('tr')].find(tr => tr.querySelector('th') && !tr.querySelector('td'));
    const lines = head ? [keep(head).map(cell).join(',')] : [];
    table.querySelectorAll('tr').forEach(tr => { if (tr.querySelector<HTMLInputElement>('.pick')?.checked) lines.push(keep(tr).map(cell).join(',')); });
    const url = URL.createObjectURL(new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv' }));
    const a = Object.assign(document.createElement('a'), { href: url, download: `${(ev.currentTarget as HTMLElement).dataset.exportSelected}.csv` });
    a.click(); URL.revokeObjectURL(url);
  });
  const bulkForm = bulkbar.closest('form');
  bulkForm?.addEventListener('submit', e => {
    if (action?.value !== 'delete' || bulkForm.dataset.confirmed) return;
    e.preventDefault(); e.stopImmediatePropagation();
    const count = allFlag?.value ? bulkbar.dataset.total : String(picks());
    const submitter = (e as SubmitEvent).submitter as HTMLButtonElement | null;
    void ask(`Delete ${count} row(s)? This cannot be undone.`, `Delete ${count}`, true).then(yes => {
      if (!yes) return;
      bulkForm.dataset.confirmed = '1';
      bulkForm.requestSubmit(submitter?.form === bulkForm ? submitter : undefined);
      delete bulkForm.dataset.confirmed;
    });
  });
  sync();
});

// Light / Dark / Auto: the sidebar pill and Account > Appearance switch at once (no reload),
// stay in step with each other, and save in the background. "system" stays on <html> like the
// server writes it: tokens.css only follows the device under data-theme="system".
const applyTheme = (next: string, form: HTMLFormElement) => {
  document.documentElement.dataset.theme = next;
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', next === 'system' ? 'light dark' : next);
  document.querySelectorAll<HTMLFormElement>('form[data-theme-pick]').forEach(pick => {
    const buttons = [...pick.querySelectorAll<HTMLButtonElement>('button[name="theme"]')];
    pick.style.setProperty('--i', String(buttons.findIndex(b => b.value === next)));
    buttons.forEach(b => b.setAttribute('aria-checked', String(b.value === next)));
  });
  document.querySelectorAll<HTMLInputElement>('form[data-theme-choice] input[name="theme"]').forEach(r => { r.checked = r.value === next; });
  const body = new URLSearchParams(new FormData(form) as any);
  body.set('theme', next);
  fetch(form.action, { method: 'POST', body, redirect: 'manual' });
};
document.querySelectorAll<HTMLFormElement>('form[data-theme-pick]').forEach(form => form.addEventListener('submit', e => {
  const button = (e as SubmitEvent).submitter as HTMLButtonElement | null;
  if (!button?.value) return;
  e.preventDefault();
  e.stopPropagation(); // not a page load: no busy spinner
  applyTheme(button.value, form);
  const menu = form.closest('details.menu'); // the appearance pop-up closes on a pick
  if (menu instanceof HTMLDetailsElement) closePop(menu);
}));
// The save button is there for pages without JavaScript.
document.querySelectorAll<HTMLFormElement>('form[data-theme-choice]').forEach(form => {
  form.querySelector<HTMLElement>('[data-theme-save]')!.hidden = true;
  form.addEventListener('change', () => applyTheme(new FormData(form).get('theme') as string, form));
});

// Copy buttons (redirect URIs, webhook URLs).
document.querySelectorAll<HTMLButtonElement>('[data-copy]').forEach(b => b.addEventListener('click', async () => {
  await navigator.clipboard.writeText(b.dataset.copy!);
  const label = b.lastChild!;
  const was = label.textContent;
  label.textContent = ' Copied';
  setTimeout(() => { label.textContent = was; }, 1500);
}));

// ---- Inline form validation -------------------------------------------------------------
// The browser's own rules (required, type, pattern, min/max, length) decide validity; this shows
// the message under the field instead of a native bubble, only checks fields that are visible,
// and focuses the first problem. data-msg overrides the message (data-missing, just the "left empty" one); data-match="<name>" means
// "must equal that field"; data-rule="sender" accepts "Name <address>" or a bare address.
const isShown = (el: HTMLElement) => !el.closest('[hidden], template') && el.getClientRects().length > 0;
const fieldLabel = (el: HTMLElement) => {
  const label = el.closest('label');
  // The label's own words, not its links or hints; a label-row (name plus a link like "Forgot it?") counts as the label.
  const own = (n: Element) => [...n.childNodes].filter(c => c.nodeType === Node.TEXT_NODE).map(c => c.textContent).join(' ').trim();
  const text = label ? own(label) || own(label.querySelector(':scope > .label-row') ?? label) : '';
  return text || el.getAttribute('aria-label') || 'This field';
};

function messageFor(el: HTMLInputElement) {
  const v = el.validity, name = fieldLabel(el);
  if (v.valueMissing) return el.dataset.missing ?? (el.type === 'checkbox' ? el.dataset.msg ?? 'Please tick this to continue' : `${name} is required`);
  if (el.dataset.msg && (v.patternMismatch || v.typeMismatch || v.customError)) return el.dataset.msg;
  if (v.typeMismatch) return el.type === 'email' ? 'Enter an email address, like name@example.org' : el.type === 'url' ? 'Enter a full web address starting with https://' : `${name} isn't valid`;
  if (v.tooShort) return `${name} needs at least ${el.minLength} characters`;
  if (v.tooLong) return `${name} can be at most ${el.maxLength} characters`;
  if (v.rangeUnderflow) return `${name} must be ${el.min} or more`;
  if (v.rangeOverflow) return `${name} must be ${el.max} or less`;
  if (v.stepMismatch) { const places = (el.step.split('.')[1] ?? '').length; return places ? `${name} can have at most ${places} decimal place${places === 1 ? '' : 's'}` : `${name} must be a whole number`; }
  if (v.badInput) return `${name} must be a number`;
  if (v.patternMismatch) return `${name} isn't in the right format`;
  return el.validationMessage;
}

function applyCustomRules(el: HTMLInputElement) {
  el.setCustomValidity('');
  const form = el.form;
  if (el.dataset.match && form) {
    const other = form.elements.namedItem(el.dataset.match) as HTMLInputElement | null;
    if (other && el.value && el.value !== other.value) el.setCustomValidity(el.dataset.msg ?? 'The two entries do not match');
  }
  if (el.dataset.requiredWith && form) {
    const other = form.elements.namedItem(el.dataset.requiredWith) as HTMLInputElement | null;
    if (other?.value && !el.value) el.setCustomValidity('Enter your current password to change it');
  }
  if (el.dataset.rule === 'sender' && el.value && !/^([^<>@]+<)?[^@\s<>]+@[^@\s<>]+\.[^@\s<>]+>?$/.test(el.value.trim())) el.setCustomValidity(el.dataset.msg ?? 'Enter an email address');
  if (el.dataset.rule === 'lines-url' && el.value.trim()) {
    const bad = el.value.split(/\s+/).filter(Boolean).find(u => { try { return !/^https?:$/.test(new URL(u).protocol); } catch { return true; } });
    if (bad) el.setCustomValidity(`"${bad.slice(0, 60)}" isn't a web address`);
  }
}

function check(el: HTMLInputElement) {
  if (!isShown(el) || el.disabled || el.type === 'hidden') return true;
  applyCustomRules(el);
  const ok = el.checkValidity();
  const holder = el.closest('label') ?? el.parentElement!;
  let err = holder.querySelector<HTMLElement>(':scope > .field-error');
  if (ok) {
    el.removeAttribute('aria-invalid');
    err?.remove();
    return true;
  }
  if (!err) {
    err = document.createElement('span');
    err.className = 'field-error';
    err.id = `err-${Math.random().toString(36).slice(2, 9)}`;
    holder.appendChild(err);
  }
  err.textContent = messageFor(el);
  el.setAttribute('aria-invalid', 'true');
  el.setAttribute('aria-describedby', err.id);
  return false;
}

const fields = (form: HTMLFormElement) => [...form.querySelectorAll<HTMLInputElement>('input, select, textarea')];

// ---- A form the server sent back (it failed validation) gets what was typed back, not blanks.
const formBack: { action: string; values: Record<string, string[]> } | null = JSON.parse(document.getElementById('formBack')?.textContent || 'null');
const backTo = formBack && [...document.querySelectorAll<HTMLFormElement>('form[method="post" i]')]
  .find(f => new URL(f.getAttribute('action') || location.pathname, location.href).pathname === formBack.action);
if (formBack && backTo) {
  const seen: Record<string, number> = {};
  fields(backTo).forEach(el => {
    const list = formBack.values[el.name];
    if (!el.name || el.type === 'hidden' || el.type === 'password' || el.type === 'file') return;
    if (el.type === 'checkbox' || el.type === 'radio') { el.checked = Boolean(list?.includes(el.value)); return; }
    if (!list) return;
    // Repeated names (rows of figures) fill in order.
    const i = seen[el.name] = (seen[el.name] ?? -1) + 1;
    if (list[i] !== undefined) el.value = list[i]!;
  });
}
document.querySelectorAll<HTMLFormElement>('form').forEach(form => {
  if ((form.getAttribute('method') ?? '').toLowerCase() !== 'post' && !form.querySelector('[required], [pattern]')) return;
  form.noValidate = true;
  form.addEventListener('submit', e => {
    if (((e as SubmitEvent).submitter as HTMLButtonElement | null)?.formNoValidate) return;
    const bad = fields(form).filter(el => !check(el));
    if (bad.length) {
      e.preventDefault();
      e.stopImmediatePropagation();
      bad[0].focus();
    }
  }, { capture: true });
  form.addEventListener('focusout', e => {
    const el = e.target as HTMLInputElement;
    if (el.matches('input, select, textarea') && (el.value || el.hasAttribute('aria-invalid'))) check(el);
  });
  form.addEventListener('input', e => {
    const el = e.target as HTMLInputElement;
    if (el.hasAttribute('aria-invalid')) check(el);
    // A confirmation field re-checks when the field it must match changes.
    form.querySelectorAll<HTMLInputElement>(`[data-match="${el.name}"][aria-invalid]`).forEach(check);
  });
});

// A column picker must keep at least one box ticked.
document.querySelectorAll<HTMLFormElement>('form[data-need-one]').forEach(form => form.addEventListener('submit', e => {
  const name = form.dataset.needOne!;
  const boxes = [...form.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`)];
  const holder = form.querySelector('fieldset') ?? form;
  holder.querySelector('.field-error')?.remove();
  if (!boxes.some(b => b.checked)) {
    e.preventDefault(); e.stopImmediatePropagation();
    const err = document.createElement('span');
    err.className = 'field-error'; err.textContent = 'Choose at least one column';
    holder.appendChild(err);
    boxes[0]?.focus();
  }
}, { capture: true }));

// ---- Filter builder: each row's conditions and value box follow the chosen column's type ----
const listNames: Record<string, string> = JSON.parse(document.getElementById('listNames')?.textContent || '{}');
function syncFilterRow(row: HTMLElement) {
  const col = row.querySelector<HTMLSelectElement>('[data-filter-col]')!;
  const op = row.querySelector<HTMLSelectElement>('[data-filter-op]')!;
  const opt = col.selectedOptions[0];
  const type = opt?.dataset.type ?? 'text';
  // Conditions that fit this column type.
  let firstValid: HTMLOptionElement | null = null;
  [...op.options].forEach(o => { const fits = o.dataset.for === type; o.hidden = !fits; o.disabled = !fits; if (fits && !firstValid) firstValid = o; });
  if (op.selectedOptions[0]?.disabled && firstValid) op.value = (firstValid as HTMLOptionElement).value;
  // The value box: none for "is empty" / yes-no, a picker for choices, a date or number input otherwise.
  const old = row.querySelector<HTMLInputElement | HTMLSelectElement>('[data-filter-val]')!;
  const needsValue = !['empty', 'not_empty', 'yes', 'no'].includes(op.value);
  const choices = type === 'list' ? Object.entries(listNames) : type === 'select' ? (opt.dataset.options || '').split('\u001f').filter(Boolean).map(v => [v, v]) : null;
  let box: HTMLInputElement | HTMLSelectElement;
  if (choices) {
    box = document.createElement('select');
    box.append(...choices.map(([v, l]) => new Option(l, v, false, v === old.value)));
  } else {
    box = document.createElement('input');
    box.type = type === 'date' ? 'date' : type === 'number' ? 'number' : 'text';
    if (type === 'number') box.step = 'any';
    box.value = old.value;
    box.maxLength = 200;
  }
  box.name = 'fv'; box.dataset.filterVal = ''; box.setAttribute('aria-label', 'Value');
  box.hidden = !needsValue;
  if (!needsValue) (box as HTMLInputElement).value = '';
  old.replaceWith(box);
}
document.querySelectorAll<HTMLElement>('[data-filterbar]').forEach(bar => {
  bar.querySelectorAll<HTMLElement>('[data-filter-row]').forEach(syncFilterRow);
  bar.addEventListener('change', e => {
    const t = e.target as HTMLElement;
    if (t.matches('[data-filter-col], [data-filter-op]')) syncFilterRow(t.closest('[data-filter-row]')!);
  });
  // New rows (from "Filter") start in sync with their default column.
  new MutationObserver(ms => ms.forEach(m => m.addedNodes.forEach(n => {
    if (n instanceof HTMLElement && n.matches('[data-filter-row]')) syncFilterRow(n);
  }))).observe(bar.querySelector('.filter-rows')!, { childList: true });
  // "Apply" shows only once there is something to apply: a filter row, or a search that changed.
  // (Enter in the search box applies it too.) Without this file it is always there.
  const apply = bar.querySelector<HTMLElement>('[data-filter-apply]'), q = bar.querySelector<HTMLInputElement>('input[name=q]');
  if (apply) {
    const start = q?.value ?? '';
    const sync = () => { apply.hidden = !bar.querySelector('.filter-rows [data-filter-row]') && (q?.value ?? '') === start; };
    bar.addEventListener('input', sync);
    new MutationObserver(sync).observe(bar.querySelector('.filter-rows')!, { childList: true });
    sync();
  }
});

// Email composer: choosing a template hides the free-text subject and message.
document.querySelectorAll<HTMLFormElement>('form[data-email-compose]').forEach(form => {
  const pick = form.querySelector<HTMLSelectElement>('[data-email-template]');
  const free = form.querySelector<HTMLElement>('[data-email-free]')!;
  const sync = () => { free.hidden = Boolean(pick?.value); };
  pick?.addEventListener('change', sync);
  sync();
});

// Platform email editor: the preview follows what is typed, before saving. form[data-preview] posts its
// fields to that URL, which answers { html, subject } for the iframe and subject line.
$$<HTMLFormElement>('form[data-preview]').forEach(form => {
  const frame = document.querySelector<HTMLIFrameElement>('.mail-frame'), subject = document.querySelector('[data-preview-subject]');
  let timer = 0, n = 0;
  form.addEventListener('input', () => {
    clearTimeout(timer);
    timer = window.setTimeout(async () => {
      const mine = ++n;
      const res = await fetch(form.dataset.preview!, { method: 'POST', body: new URLSearchParams(new FormData(form) as unknown as Record<string, string>), headers: { accept: 'application/json' } }).catch(() => null);
      if (!res?.ok || mine !== n) return; // an older answer arriving late doesn't undo a newer one
      const out = await res.json() as { html: string; subject: string };
      if (frame) frame.srcdoc = out.html;
      if (subject) subject.textContent = out.subject;
    }, 400);
  });
});

// A select shows only the notes for its chosen option: [data-reveal=x] drives [data-reveal-of=x][data-when=value].
$$<HTMLSelectElement>('select[data-reveal]').forEach(sel => {
  const sync = () => $$<HTMLElement>(`[data-reveal-of="${sel.dataset.reveal}"]`).forEach(el => { el.hidden = el.dataset.when !== sel.value; });
  sel.addEventListener('change', sync);
  sync();
});

// A select that changes what the page shows (list reach for a template) submits its GET form.
$$<HTMLSelectElement>('select[data-autosubmit]').forEach(sel => sel.addEventListener('change', () => sel.form?.requestSubmit()));

// Import, match step: two headers can't go into the same column. Mark both and hold the button
// until one changes (the server checks the same thing).
const matchTable = document.querySelector<HTMLTableElement>('table[data-match]');
if (matchTable) {
  const selects = [...matchTable.querySelectorAll<HTMLSelectElement>('select[name=to]')];
  selects.forEach(s => { s.dataset.initial = s.value; });
  const submit = document.querySelector<HTMLButtonElement>('[data-match-submit]')!;
  const sync = () => {
    const taken = new Map<string, number>();
    selects.forEach(s => { if (s.value && s.value !== '@new') taken.set(s.value, (taken.get(s.value) ?? 0) + 1); });
    let clash = false;
    selects.forEach(s => {
      const bad = (taken.get(s.value) ?? 0) > 1;
      clash ||= bad;
      s.toggleAttribute('aria-invalid', bad);
      if (bad) s.setAttribute('aria-invalid', 'true');
      // The server's note describes the choice it rendered; once changed it no longer applies.
      const note = s.parentElement!.querySelector<HTMLElement>('.field-note');
      if (note) note.hidden = s.value !== s.dataset.initial;
    });
    submit.disabled = clash;
  };
  selects.forEach(s => s.addEventListener('change', sync));
  sync();
}

// A link to a closed <details> (e.g. "New list") opens it.
document.addEventListener('click', e => {
  const a = (e.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
  const target = a && a.hash.length > 1 ? document.getElementById(a.hash.slice(1)) : null;
  if (target instanceof HTMLDetailsElement) target.open = true;
});

// Formatting toolbars load only on pages with a free-text box (a separate chunk).
if (document.querySelector('textarea[data-rich]')) void import('./rich-text.js');

// "Print or save as PDF" on the giving statement.
document.querySelector('[data-print]')?.addEventListener('click', () => print());

// File uploads: <input type="file" data-upload="/url"> posts each chosen file as the request body.
// data-upload-into="<textarea name>" adds the uploaded image's address there (event images);
// otherwise the page reloads to show the new files. data-confirm-upload asks first.
const csrfToken = () => document.querySelector<HTMLInputElement>('input[name="_csrf"]')?.value ?? '';
document.querySelectorAll<HTMLInputElement>('input[type=file][data-upload]').forEach(input => input.addEventListener('change', async () => {
  const chosen = [...(input.files ?? [])];
  if (!chosen.length || (input.dataset.confirmUpload && !(await ask(input.dataset.confirmUpload, 'Upload')))) { input.value = ''; return; }
  const into = input.dataset.uploadInto ? input.form?.querySelector<HTMLTextAreaElement>(`textarea[name="${input.dataset.uploadInto}"]`) ?? null : null;
  const label = input.closest('label');
  label?.setAttribute('aria-busy', 'true');
  try {
    for (const file of chosen) {
      // data-upload-caption="#field": that field's text goes along as ?caption= (WhatsApp attachments).
      const captionEl = input.dataset.uploadCaption ? document.querySelector<HTMLInputElement>(input.dataset.uploadCaption) : null;
      const target = captionEl?.value.trim() ? `${input.dataset.upload}${input.dataset.upload!.includes('?') ? '&' : '?'}caption=${encodeURIComponent(captionEl.value.trim())}` : input.dataset.upload!;
      const res = await fetch(target, { method: 'POST', body: file, headers: {
        'content-type': file.type || 'application/octet-stream', 'x-filename': encodeURIComponent(file.name), 'x-csrf-token': csrfToken(), accept: 'application/json' } });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) { alert(`${file.name}: ${body.error || (res.status === 413 ? 'the file is too big' : `upload failed (${res.status})`)}`); return; }
      if (into) into.value = [into.value.trim(), body.url].filter(Boolean).join('\n');
    }
    if (!into) location.reload();
  } finally { label?.removeAttribute('aria-busy'); input.value = ''; }
}));

// The check-in scanner loads only on the check-in page (a separate chunk).
if (document.querySelector('[data-scanner]')) void import('./scanner.js');

// Platform > Plans: the "customers already on it" choice shows once a value that affects them changes.
document.querySelectorAll<HTMLFormElement>('form[data-plan-form]').forEach(form => {
  const policy = form.querySelector<HTMLElement>('[data-policy]');
  if (!policy) return;
  const watched = [...form.querySelectorAll<HTMLInputElement>('input[data-was]')];
  const sync = () => { policy.hidden = !watched.some(i => i.value.trim() !== i.dataset.was); };
  watched.forEach(i => i.addEventListener('input', sync));
  sync();
});

// Site pages editor: a colour picker and its #hex box stay in step.
document.querySelectorAll<HTMLInputElement>('input[data-colour-for]').forEach(picker => {
  const box = document.querySelector<HTMLInputElement>(`input[name="${CSS.escape(picker.dataset.colourFor!)}"]`);
  if (!box) return;
  picker.addEventListener('input', () => { box.value = picker.value; });
  box.addEventListener('input', () => { if (/^#[0-9a-f]{6}$/i.test(box.value)) picker.value = box.value; });
});

// WhatsApp "Connect with Facebook" loads Facebook's SDK only on that page (a separate chunk).
if (document.querySelector('[data-embedded-signup]')) void import('./embedded-signup.js');
if (document.querySelector('[data-leads-signup]')) void import('./meta-leads.js');

// WhatsApp: "typing…" for the supporter (and blue ticks on what they sent) once someone starts a
// reply. Meta shows it for up to 25 seconds, so it is sent again at most every 20.
document.querySelectorAll<HTMLElement>('[data-typing]').forEach(box => {
  let last = 0;
  box.closest('form')?.addEventListener('input', () => {
    if (Date.now() - last < 20_000) return;
    last = Date.now();
    void fetch(box.dataset.typing!, { method: 'POST', headers: { 'x-csrf-token': csrfToken(), accept: 'application/json' } }).catch(() => {});
  });
});

// Broadcasts: each step shows a template for email, WhatsApp and SMS (only that channel's), or a
// message box for Instagram and Apple Messages.
document.querySelectorAll<HTMLSelectElement>('[data-bc-channel]').forEach(sel => {
  const li = sel.closest('li')!;
  const tpl = li.querySelector<HTMLElement>('[data-bc-template]')!, txt = li.querySelectorAll<HTMLElement>('[data-bc-text]');
  const sync = () => {
    const ch = sel.value, free = ch === 'instagram' || ch === 'apple';
    tpl.hidden = !ch || free; txt.forEach(t => { t.hidden = !free; });
    tpl.querySelectorAll<HTMLOptionElement>('option[data-channel]').forEach(o => { o.hidden = o.dataset.channel !== ch; });
    // A template picked for the previous channel would otherwise stay selected, out of sight.
    const pick = tpl.querySelector('select');
    if (pick?.selectedOptions[0]?.hidden) pick.value = '';
  };
  sel.addEventListener('change', sync);
  sync();
});

// A "From" list offers only the accounts that send on the chosen template's channel. Delegated:
// workflow steps are added after load. Server side, a sender that can't send the channel is refused.
const syncSender = (tpl: HTMLSelectElement) => {
  const from = tpl.closest('.row, li, .send-row')?.querySelector<HTMLSelectElement>('select[data-sender]');
  const ch = tpl.selectedOptions[0]?.dataset.channel;
  if (!from) return;
  from.querySelectorAll<HTMLOptionElement>('option[data-channels]').forEach(o => { o.hidden = Boolean(ch) && !o.dataset.channels!.split(' ').includes(ch!); });
  if (from.selectedOptions[0]?.hidden) from.value = '';
};
document.querySelectorAll<HTMLSelectElement>('select[data-sender-template]').forEach(syncSender);
document.addEventListener('change', e => { const t = e.target as HTMLElement; if (t.matches?.('select[data-sender-template]')) syncSender(t as HTMLSelectElement); });

// Policy editor: "every action" hides the grid; "all of an area" ticks and locks its actions.
document.querySelectorAll<HTMLFormElement>('form[data-policy-form]').forEach(form => {
  const all = form.querySelector<HTMLInputElement>('[data-all-actions]');
  const grid = form.querySelector<HTMLElement>('[data-action-grid]');
  all?.addEventListener('change', () => { if (grid) grid.hidden = all.checked; });
  form.querySelectorAll<HTMLInputElement>('[data-whole-area]').forEach(w => {
    const sync = () => w.closest('fieldset')!.querySelectorAll<HTMLInputElement>('input[name="actions"]').forEach(a => {
      if (w.checked) { a.checked = true; a.dataset.locked = '1'; a.disabled = true; } else if (a.dataset.locked) { a.disabled = false; delete a.dataset.locked; }
    });
    w.addEventListener('change', sync); sync();
  });
});

// Record gift: choosing a pledge or recurring gift fills in its fund (data-fund on the option).
document.querySelectorAll<HTMLSelectElement>('select[data-sets-fund]').forEach(sel => sel.addEventListener('change', () => {
  const fund = sel.selectedOptions[0]?.dataset.fund;
  const input = sel.form?.querySelector<HTMLInputElement>('input[name="fund"]');
  if (fund && input) input.value = fund;
}));

// A button that opens a small form in a <dialog> (the refund box): data-open-dialog="<dialog id>".
document.querySelectorAll<HTMLElement>('[data-open-dialog]').forEach(b => b.addEventListener('click', () =>
  document.querySelector<HTMLDialogElement>(`dialog#${CSS.escape(b.dataset.openDialog!)}`)?.showModal()));

// ---------- merge-field chips: <button data-insert="{{First name}}" data-target="textarea id"> ----------
// Puts the field where the cursor is: into the rich-text editor when the box has one, else the textarea.
document.addEventListener('click', e => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-insert]');
  const area = b && document.getElementById(b.dataset.target ?? '') as (HTMLTextAreaElement & { rtInsert?: (t: string) => void }) | null;
  if (!b || !area) return;
  const tok = b.dataset.insert!;
  if (area.rtInsert) return area.rtInsert(tok);
  const at = area.selectionStart ?? area.value.length;
  area.value = area.value.slice(0, at) + tok + area.value.slice(area.selectionEnd ?? at);
  area.focus();
  area.selectionStart = area.selectionEnd = at + tok.length;
  area.dispatchEvent(new Event('input', { bubbles: true }));
});
document.addEventListener('mousedown', e => { if ((e.target as HTMLElement).closest('button[data-insert]')) e.preventDefault(); }); // keep the cursor where it was

// ---------- New message pop-up: search as you type, pick the person, then their channels ----------
const nm = document.querySelector<HTMLDialogElement>('dialog[data-nm]');
if (nm) {
  const form = nm.querySelector<HTMLFormElement>('[data-nm-form]')!;
  const q = nm.querySelector<HTMLInputElement>('[data-nm-q]')!;
  const status = nm.querySelector<HTMLElement>('[data-nm-status]')!;
  const results = nm.querySelector<HTMLElement>('[data-nm-results]')!;
  const back = nm.querySelector<HTMLButtonElement>('[data-nm-back]')!;
  const search = nm.querySelector<HTMLElement>('[data-nm-search]')!;
  const hint = status.textContent ?? '';
  let timer: ReturnType<typeof setTimeout> | undefined, ctrl: AbortController | undefined, last = '';

  const unpick = () => {
    nm.classList.remove('is-picked');
    nm.querySelectorAll('[data-nm-match].is-picked').forEach(li => li.classList.remove('is-picked'));
    nm.querySelectorAll<HTMLElement>('[data-nm-channels]').forEach(el => { el.hidden = true; });
    search.hidden = false; back.hidden = true;
  };
  const run = async () => {
    const term = q.value.trim();
    if (term === last) return;
    last = term;
    ctrl?.abort();
    unpick();
    if (term.length < 2) { results.innerHTML = ''; status.textContent = hint; return; }
    ctrl = new AbortController();
    status.textContent = 'Searching…';
    try {
      const res = await fetch(`/inbox/new?partial=1&q=${encodeURIComponent(term)}`, { signal: ctrl.signal, headers: { accept: 'text/html' } });
      if (!res.ok) throw new Error(String(res.status));
      results.innerHTML = await res.text(); // our own server-rendered, escaped fragment
      const n = results.querySelectorAll('[data-nm-match]').length;
      status.textContent = n ? `${n === 20 ? 'The first 20' : n} ${n === 1 ? 'person matches' : 'people match'}. Choose who to write to.` : '';
    } catch (e) {
      if ((e as Error).name !== 'AbortError') status.textContent = "That search didn't work. Try again in a moment.";
    }
  };
  q.addEventListener('input', () => { clearTimeout(timer); timer = setTimeout(run, 250); });
  // Enter searches straight away instead of leaving for the full page.
  form.addEventListener('submit', e => { e.preventDefault(); clearTimeout(timer); last = ''; void run(); });
  results.addEventListener('click', e => {
    const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-nm-pick]');
    const li = b?.closest<HTMLElement>('[data-nm-match]');
    if (!li) return;
    nm.classList.add('is-picked'); li.classList.add('is-picked');
    const ch = li.querySelector<HTMLElement>('[data-nm-channels]')!;
    ch.hidden = false; search.hidden = true; back.hidden = false;
    ch.querySelector<HTMLElement>('a.nm-ch')?.focus();
  });
  back.addEventListener('click', () => { unpick(); q.focus(); });
}

// ---- stale pages: if something this page is showing has changed since it was rendered — in another
// tab, or by another person — say so as soon as the tab comes back into view, and offer to reload.
// This is the friendly early warning; the save itself is refused by the server (lib/revision.ts),
// so nothing is overwritten even if the warning is missed or JavaScript is off.
(() => {
  const csrf = document.querySelector<HTMLInputElement>('form input[name="_csrf"]')?.value;
  const main = document.getElementById('main');
  if (!csrf || !main) return;
  let warned = false;
  let lastCheck = 0;

  const show = (gone: boolean) => {
    if (warned) return;
    warned = true;
    const note = document.createElement('div');
    note.className = 'notice warn';
    note.setAttribute('role', 'alert');
    const body = document.createElement('div');
    const head = document.createElement('b');
    head.textContent = 'This page is out of date. ';
    body.append(head, document.createTextNode(gone
      ? 'Something you are looking at has been deleted since this page was loaded. Reload to see the latest.'
      : 'Someone changed something you are looking at, in another tab or by another person. Reload to see the latest before you make changes.'));
    const reload = document.createElement('button');
    reload.type = 'button'; reload.className = 'secondary'; reload.textContent = 'Reload this page';
    reload.addEventListener('click', () => location.reload());
    body.append(' ', reload);
    note.append(body);
    main.prepend(note);
  };

  const check = async () => {
    if (warned || document.hidden || Date.now() - lastCheck < 5000) return;
    const tokens = [...document.querySelectorAll<HTMLInputElement>('input[name="_rev"]')].map(i => i.value).filter(Boolean);
    if (!tokens.length) return;
    lastCheck = Date.now();
    try {
      const body = new URLSearchParams(); body.set('_csrf', csrf);
      for (const t of tokens) body.append('_rev', t);
      const res = await fetch('/rev/check', { method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/x-www-form-urlencoded', accept: 'application/json' }, body });
      if (!res.ok) return;
      const { stale, gone } = await res.json() as { stale?: string[]; gone?: string[] };
      if (stale?.length) show(false);
      else if (gone?.length) show(true);
    } catch { /* offline or blocked: the next focus tries again */ }
  };

  window.addEventListener('focus', () => void check());
  window.addEventListener('pageshow', () => void check());
  document.addEventListener('visibilitychange', () => { if (!document.hidden) void check(); });
})();

// Settings > Colour: suggest colours from the uploaded logo. Draw it small, skip transparent, near-white,
// near-black and grey pixels, bucket the rest and offer the five most common as swatches.
document.querySelectorAll<HTMLElement>('[data-logo-swatches]').forEach(box => {
  const img = new Image();
  img.onload = () => {
    const c = document.createElement('canvas'), size = 64;
    c.width = c.height = size;
    const ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.drawImage(img, 0, 0, size, size);
    const buckets = new Map<number, number>();
    const d = ctx.getImageData(0, 0, size, size).data;
    for (let i = 0; i < d.length; i += 4) {
      const [r, g, b] = [d[i]!, d[i + 1]!, d[i + 2]!], max = Math.max(r, g, b), min = Math.min(r, g, b);
      if (d[i + 3]! < 200 || min > 235 || max < 30 || max - min < 40) continue;
      const k = ((r >> 5) << 6) | ((g >> 5) << 3) | (b >> 5);
      buckets.set(k, (buckets.get(k) ?? 0) + 1);
    }
    const pick = [...buckets].sort((a, b) => b[1] - a[1]).map(([k]) => [(k >> 6) & 7, (k >> 3) & 7, k & 7].map(v => v * 32 + 16));
    const out: number[][] = [];
    // Skip near-duplicates so five swatches are five different colours.
    for (const p of pick) if (out.length < 5 && out.every(q => Math.abs(q[0]! - p[0]!) + Math.abs(q[1]! - p[1]!) + Math.abs(q[2]! - p[2]!) > 90)) out.push(p);
    const row = box.querySelector('.swatch-row'), field = document.querySelector<HTMLInputElement>(`input[name="${box.dataset.into}"]`);
    if (!out.length || !row || !field) return;
    for (const p of out) {
      const hex = '#' + p.map(v => v.toString(16).padStart(2, '0')).join('');
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'swatch'; b.style.background = hex; b.title = hex; b.setAttribute('aria-label', `Use ${hex}`);
      b.addEventListener('click', () => { field.value = hex; field.dispatchEvent(new Event('input', { bubbles: true })); });
      row.append(b);
    }
    box.hidden = false;
  };
  img.src = box.dataset.logoSwatches!;
});
